import * as React from "react"
import { Text } from "components/Text"
import { Anchor } from "components/Icon/Anchor"
import { useTranslator } from "components/Translator"
import { Button } from "components/Button"
import * as MediaType from "enums/MediaType"
import { ProfilePage } from "components/ProfilePage"
import { MediasCarousel } from "components/Carousel"
import { Map, MapboxProvider, MapModal, Marker } from "components/Map"
import { BoatImages } from "domains/boat/BoatImages"
import { BoatGeneral } from "domains/boat/BoatGeneral"
import { BoatDescription } from "domains/boat/BoatDescription"
import { BoatComfort } from "domains/boat/BoatComfort"
import { BoatEnergy } from "domains/boat/BoatEnergy"
import { BoatSafety } from "domains/boat/BoatSafety"
import { BoatHeader } from "domains/boat/BoatHeader"
import { Section } from "components/Section"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { useRouter } from "components/Router"
import { Pen, PlusCircle } from "components/Icon"
import { BoatDeleteButton } from "domains/boat/BoatDeleteButton"
import { formatAddress } from "utils/formatAddress"
import { BoatPosition } from "domains/boat/BoatPosition"

export function AccountBoatShowPage({
  boat,
  user,
  tabs = window.accountNav,
  infos,
}) {
  const connectedUser = useConnectedUser()
  const router = useRouter()
  const translator = useTranslator()

  const handleDeleteBoat = () => {
    router.redirect(
      router.generate("app_account_boat_index", { userId: user.id }),
    )
  }

  const isOwnProfile = connectedUser?.id === user.id
  const isAdmin = connectedUser?.roles.includes("ROLE_ADMIN")

  const extraActions = [
    isOwnProfile || isAdmin ? (
      <Button
        key="add-boat"
        href={
          connectedUser?.id === user.id
            ? router.generate("app_user_boat_add")
            : router.generate("app_user_boat_add", { userId: user.id })
        }
        iconLeft={<PlusCircle className="w-4" />}
      >
        {translator.trans("account.boat.show.add", null, "pages")}
      </Button>
    ) : null,
    isOwnProfile || isAdmin ? (
      <Button
        key="edit-boat"
        variant="outline"
        color="secondary"
        href={router.generate("app_user_boat_edit", { boatId: boat.id })}
        iconLeft={<Pen className="w-4" />}
      >
        {translator.trans("account.boat.show.edit", null, "pages")}
      </Button>
    ) : null,
    isOwnProfile || isAdmin ? (
      <BoatDeleteButton
        key="delete-boat"
        boat={boat}
        onDelete={handleDeleteBoat}
        variant="outline"
      />
    ) : null,
  ].filter(Boolean)

  return (
    <ProfilePage
      user={user}
      tabs={tabs}
      infos={infos}
      extraActions={extraActions}
    >
      <div className="space-y-6 lg:space-y-10">
        <div className="space-y-4">
          <div className="space-y-2">
            <BoatHeader boat={boat} />
            {boat.completionRate ? (
              <Text className="text-default" variant="body2">
                {translator.trans(
                  "account.boat.show.boatCompletionRate",
                  { boatCompletionRate: boat.completionRate },
                  "pages",
                )}
              </Text>
            ) : null}
            <div className="flex flex-col space-y-2">
              <Text className="text-default" variant="caption">
                {boat.nbVisits ? (
                  <span>
                    {boat.nbVisits}{" "}
                    {translator.trans("account.boat.show.views", null, "pages")}
                  </span>
                ) : null}
              </Text>
            </div>
          </div>
          <BoatImages boat={boat} />
        </div>

        <BoatGeneral boat={boat} />
        <BoatPort boat={boat} />
        <BoatPosition boat={boat} />
        <BoatVideos boat={boat} />
        <BoatDescription boat={boat} />
        <BoatComfort boat={boat} />
        <BoatEnergy boat={boat} />
        <BoatSafety boat={boat} />
      </div>
    </ProfilePage>
  )
}

const BoatPort = ({ boat }) => {
  const translator = useTranslator()

  if (!boat.address) {
    return null
  }

  return (
    <Section>
      <Section.Title>
        {translator.trans("account.boat.show.port", null, "pages")}
      </Section.Title>
      <div className="flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:items-center lg:justify-between">
        <div className="flex flex-row space-x-2 items-center">
          <Anchor className="text-primary-default w-6" />
          <Text variant="body2" aria-label="Port du bateau">
            {formatAddress(boat.address)}
          </Text>
        </div>
        <div className="w-full lg:w-auto">
          {boat.address.coords.lat && boat.address.coords.lng ? (
            <MapboxProvider accessToken={process.env.MAPBOX_ACCESS_TOKEN}>
              <MapModal
                trigger={
                  <Button fullWidth variant="outline">
                    {translator.trans(
                      "account.boat.show.showMap",
                      null,
                      "pages",
                    )}
                  </Button>
                }
                map={
                  <Map
                    initialViewState={{
                      latitude: boat.address.coords.lat,
                      longitude: boat.address.coords.lng,
                      zoom: 8,
                    }}
                  >
                    <Marker
                      latitude={boat.address.coords.lat}
                      longitude={boat.address.coords.lng}
                      variant="departure"
                    />
                  </Map>
                }
                title={translator.trans(
                  "account.boat.show.portMap",
                  null,
                  "pages",
                )}
              />
            </MapboxProvider>
          ) : null}
        </div>
      </div>
    </Section>
  )
}

const BoatVideos = ({ boat }) => {
  const videos = boat.album
    ? boat.album.medias.filter(
        (media) => media.type.enum_value === MediaType.VIDEO,
      )
    : []

  const translator = useTranslator()
  const ref = React.useRef(null)

  const stopVideos = () => {
    if (!ref.current) {
      return
    }

    const videoElements = Array.from(ref.current.querySelectorAll("video"))
    videoElements.forEach((videoElement) => videoElement.pause())
  }

  if (videos.length === 0) {
    return null
  }

  return (
    <Section>
      <Section.Title>
        {translator.trans("account.boat.show.video", null, "pages")}
      </Section.Title>
      <div className="h-0 relative" style={{ paddingTop: "56.25%" }} ref={ref}>
        <MediasCarousel
          className="absolute top-0 left-0 w-full h-full"
          medias={videos}
          onChange={stopVideos}
        />
      </div>
    </Section>
  )
}
